import { useState, useContext, useEffect, useRef } from "react";
import { X, Mail, Gift, Clock, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { AppContext } from "../App";
import { Turnstile } from "react-turnstile";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const NewsletterPopup = ({ onClose }) => {
  const { settings } = useContext(AppContext);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  // Check if offer is valid
  const isOfferValid = () => {
    if (!settings?.popup_offer_valid_from && !settings?.popup_offer_valid_until) {
      return true; // No date restrictions
    }
    const now = new Date();
    if (settings?.popup_offer_valid_from) {
      const from = new Date(settings.popup_offer_valid_from);
      if (now < from) return false;
    }
    if (settings?.popup_offer_valid_until) {
      const until = new Date(settings.popup_offer_valid_until);
      if (now > until) return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Bitte E-Mail eingeben");
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error("Bitte bestätigen Sie, dass Sie kein Roboter sind.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email,
        privacy_accepted: true,
        turnstile_token: turnstileToken
      });
      setSuccess(true);
      toast.success("Fast geschafft! Bitte bestätige deine E-Mail.");
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      if (error.response?.data?.detail === "Already subscribed") {
        toast.info("Du bist bereits angemeldet!");
        setTimeout(() => onClose(), 1500);
      } else {
        toast.error("Fehler bei der Anmeldung");
      }
    } finally {
      setLoading(false);
    }
  };

  const showOffer = isOfferValid() && settings?.popup_offer_enabled !== false;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pt-safe pb-safe bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
      <div 
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-monaco-bg-1 rounded-2xl border border-white/10 shadow-2xl animate-in zoom-in-95 duration-300"
        data-testid="newsletter-popup"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          data-testid="popup-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex flex-col md:flex-row">
          {/* Left Side - Offer (40%) */}
          {showOffer && (
            <div className="md:w-[40%] p-5 md:p-8 bg-gradient-to-br from-monaco-red/20 to-monaco-red/5 border-b md:border-b-0 md:border-r border-white/10 relative overflow-hidden">
              {/* Animated Background Effects */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-monaco-red rounded-full blur-3xl opacity-30 animate-pulse" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-monaco-gold rounded-full blur-2xl opacity-20 animate-pulse" style={{ animationDelay: "1s" }} />
              </div>

              {/* Floating Particles */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-1 h-1 bg-monaco-gold rounded-full animate-float opacity-60"
                    style={{
                      left: `${15 + i * 20}%`,
                      top: `${25 + (i % 2) * 30}%`,
                      animationDelay: `${i * 0.5}s`,
                      animationDuration: `${3 + i * 0.5}s`
                    }}
                  />
                ))}
              </div>

              <div className="relative">
                {/* Badge */}
                {settings?.popup_offer_badge && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-monaco-red text-white text-xs font-bold mb-3 animate-pulse shadow-lg shadow-monaco-red/50">
                    <Sparkles className="w-3 h-3" />
                    {settings.popup_offer_badge}
                  </div>
                )}

                <div className="flex items-center gap-2 mb-2">
                  <Gift className="w-4 h-4 text-monaco-red" />
                  <span className="text-xs text-monaco-text-2 uppercase tracking-wider">Angebot</span>
                </div>

                <h3 className="font-display text-xl md:text-2xl text-monaco-text-0 mb-3">
                  {settings?.popup_offer_title || "Exklusives Angebot"}
                </h3>

                {/* PRICE - Hero Element with Glow */}
                <div className="relative mb-3">
                  {/* Glow Background */}
                  <div className="absolute inset-0 bg-monaco-red/30 blur-2xl rounded-full scale-150 animate-pulse" />
                  
                  {/* Price Container */}
                  <div className="relative bg-gradient-to-r from-monaco-red/20 via-monaco-red/10 to-transparent rounded-xl p-3 border border-monaco-red/30 backdrop-blur-sm">
                    {/* Shimmer Effect */}
                    <div className="absolute inset-0 overflow-hidden rounded-xl">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer" />
                    </div>
                    
                    {/* Price Text */}
                    <div className="relative">
                      <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-monaco-red via-red-400 to-monaco-red animate-gradient drop-shadow-[0_0_30px_rgba(255,27,61,0.8)]">
                        {settings?.popup_offer_price || "60€"}
                      </div>
                      <div className="text-lg font-bold text-monaco-text-0 mt-1">
                        {settings?.popup_offer_duration || "20 Minuten"}
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-monaco-text-2 mb-3 leading-relaxed">
                  {settings?.popup_offer_description || "Nur für kurze Zeit!"}
                </p>

                {/* Validity Date Range */}
                {(settings?.popup_offer_valid_from || settings?.popup_offer_valid_until) && (
                  <div className="flex items-center gap-2 text-xs text-monaco-red">
                    <Clock className="w-3 h-3" />
                    <span>
                      {settings?.popup_offer_valid_from && settings?.popup_offer_valid_until ? (
                        `${new Date(settings.popup_offer_valid_from).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit" })}-${new Date(settings.popup_offer_valid_until).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" })}`
                      ) : settings?.popup_offer_valid_until ? (
                        `bis ${new Date(settings.popup_offer_valid_until).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" })}`
                      ) : ""}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Right Side - Newsletter (60%) */}
          <div className={`${showOffer ? "md:w-[60%]" : "w-full"} p-5 md:p-8`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-monaco-red/10 flex items-center justify-center">
                <Mail className="w-6 h-6 text-monaco-red" />
              </div>
              <div>
                <h3 className="font-display text-xl text-monaco-text-0">Newsletter</h3>
                <p className="text-sm text-monaco-text-2">Immer informiert</p>
              </div>
            </div>

            {success ? (
              <div className="py-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-green-500" />
                </div>
                <h4 className="text-lg font-semibold text-monaco-text-0 mb-2">
                  Fast geschafft!
                </h4>
                <p className="text-sm text-monaco-text-2">
                  Wir haben dir eine Bestätigungs-E-Mail gesendet.
                </p>
              </div>
            ) : (
              <>
                <p className="text-monaco-text-1 mb-6 leading-relaxed">
                  Melde dich für unseren Newsletter an und erhalte:
                </p>

                <ul className="space-y-3 mb-6">
                  {[
                    "Exklusive Angebote & Rabatte",
                    "Neue Girls der Woche",
                    "VIP Events & Specials"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-monaco-text-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-monaco-red flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="deine@email.at"
                    className="bg-monaco-bg-2 border-white/10 text-monaco-text-0 h-12 text-base"
                    data-testid="popup-email-input"
                  />
                  {TURNSTILE_SITE_KEY && (
                    <div data-testid="popup-turnstile" style={{ transform: 'scale(0.85)', transformOrigin: 'left top', marginBottom: '-8px' }}>
                      <Turnstile
                        ref={turnstileRef}
                        sitekey={TURNSTILE_SITE_KEY}
                        onVerify={(token) => setTurnstileToken(token)}
                        onExpire={() => setTurnstileToken(null)}
                        theme="dark"
                      />
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-monaco-red hover:bg-monaco-red-hover text-white font-semibold text-base"
                    data-testid="popup-subscribe-btn"
                  >
                    {loading ? "Wird angemeldet..." : "Jetzt anmelden"}
                  </Button>
                </form>

                <p className="text-xs text-monaco-text-2 mt-4 text-center">
                  Mit der Anmeldung akzeptierst du unsere Datenschutzerklärung.
                  Du kannst dich jederzeit abmelden.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
